import * as React from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import FormControl from '@mui/material/FormControl';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import * as yup from 'yup';
import { en, fr, es } from 'yup-locales';
import { alpha, styled } from '@mui/material/styles';

import { languageVar } from '@context';

const locales: Record<'fr' | 'es' | 'en', any> = { en, fr, es };

interface LanguageItemProps {
  handleCloseDrawer: () => void;
}

export function LanguageItem(props: Readonly<LanguageItemProps>) {
  const { handleCloseDrawer } = props;
  const { t, i18n } = useTranslation('common');
  const defaultLanguage = i18n.language ?? navigator.language.split('-')[0];
  const [locked, setLocked] = React.useState(true);
  const [language, setLanguage] = React.useState(defaultLanguage);
  const initialLang = React.useRef(defaultLanguage);
  const theme = useTheme();

  const handleChangeLanguage = (event: SelectChangeEvent<string>) => {
    setLanguage(event.target.value);
    languageVar(event.target.value);
  };

  const handleLock = (event: any) => {
    setLocked(!locked);
  };

  React.useEffect(() => {
    yup.setLocale(locales[language as 'fr' | 'en' | 'es']);
    i18n.changeLanguage(language);

    if (language !== initialLang.current) {
      initialLang.current = language;
      handleCloseDrawer();
    }
  }, [language]);

  return (
    <ListItem disablePadding={locked}>
      {locked ? (
        <ListItemButton onClick={handleLock} data-id="user-settings">
          <ListItemIcon>
            <theme.icons.language fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('word.language')} />
        </ListItemButton>
      ) : (
        <CssFormControl fullWidth size="small">
          <InputLabel id="language-select-label">{t('word.language')}</InputLabel>
          <Select
            labelId="language-select-label"
            id="language-select"
            value={language}
            label="Langue"
            open={!locked}
            onClose={handleLock}
            onChange={handleChangeLanguage}
            MenuProps={{
              elevation: 5,
              sx: {
                '& .MuiMenu-list': {
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.default,
                },
                '& .MuiMenu-list li:hover': {
                  color: 'white',
                  backgroundColor: alpha(theme.palette.text.secondary, 0.3),
                },
                '&& .Mui-selected': {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                },
                '&& .Mui-selected:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                },
              },
            }}
          >
            <MenuItem value={'fr'}>Français</MenuItem>
            <MenuItem value={'en'}>English</MenuItem>
            <MenuItem value={'es'}>Español</MenuItem>
          </Select>
        </CssFormControl>
      )}
    </ListItem>
  );
}

interface PrivacyPolicyItemProps {
  handleCloseDrawer: () => void;
}

export function PrivacyPolicyItem(props: Readonly<PrivacyPolicyItemProps>) {
  const { handleCloseDrawer } = props;
  const { t } = useTranslation('authentication');

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to="/privacy-policy"
        preventScrollReset={true}
        onClick={handleCloseDrawer}
      >
        <ListItemText primary={t('signup.legalDialog.Privacy Policy')} />
      </ListItemButton>
    </ListItem>
  );
}

interface TermsOfServiceItemProps {
  handleCloseDrawer: () => void;
}

export function TermsOfServiceItem(props: Readonly<TermsOfServiceItemProps>) {
  const { handleCloseDrawer } = props;
  const { t } = useTranslation('authentication');

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to="/terms-of-service"
        preventScrollReset={true}
        onClick={handleCloseDrawer}
      >
        <ListItemText primary={t('signup.legalDialog.General Conditions of Use')} />
      </ListItemButton>
    </ListItem>
  );
}

interface RegisterSpotItemProps {
  handleCloseDrawer: () => void;
}

export function RegisterSpotItem(props: Readonly<RegisterSpotItemProps>) {
  const { handleCloseDrawer } = props;
  const { t } = useTranslation('authentication');
  const theme = useTheme();

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to="/register/spot"
        preventScrollReset={true}
        onClick={handleCloseDrawer}
      >
        <ListItemIcon>
          <theme.icons.add fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('signup.Register my spot')} />
      </ListItemButton>
    </ListItem>
  );
}

const CssFormControl = styled(FormControl)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '& label.Mui-focused, svg': {
    color: '#999999',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#B2BAC2',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#434343',
    },
    '&:hover fieldset': {
      borderColor: '#666666',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#999999',
    },
  },
}));
